/**
 *  This composable acts as the single source of truth for determining if a given
 *  delivery is headed to a parcel shop, and if so, which specific parcel shop
 *  it is. It handles different scenarios where a parcel shop delivery might be
 *  indicated.
 */
import { computed } from 'vue';
import { useTrackingDataStore } from '@/stores/trackingData.js';
import {
  addressEqualsParcelShop,
  getParcelShopFromStatusAddress,
  statusAddressToParcelShop,
  hasCoordinates
} from './addressUtils.js';

export const useParcelShop = () => {
  const trackingDataStore = useTrackingDataStore();

  const redirectType = computed(() => trackingDataStore.data.redirectType);

  const parcelShopAsRedirectAddress = computed(() => {
    if (!trackingDataStore.data.redirectAddress) return null;
    const parcelShop = getParcelShopFromStatusAddress(
      trackingDataStore.data.redirectAddress,
      trackingDataStore.data.nearParcelShops
    );
    return parcelShop?.parcelShopId ? parcelShop : null;
  });

  const redirectDestinationFallback = computed(() => {
    if (!trackingDataStore.data.redirectAddress) {
      return null;
    }

    const parcelShop = statusAddressToParcelShop(
      trackingDataStore.data.redirectAddress
    );

    return {
      ...parcelShop,
      type: redirectType.value === 'PARCEL_LOCKER' ? 'LOCKER' : 'PARCEL_SHOP'
    };
  });

  const isRedirectedToParcelShop = computed(() => {
    return (
      trackingDataStore.data.status?.detail === 'PARCEL_SHOP' ||
      Boolean(trackingDataStore.data.redirectAddress)
    );
  });

  const isRedirectedToLocker = computed(() => {
    if (redirectType.value) {
      return redirectType.value === 'PARCEL_LOCKER';
    }

    return destinationParcelShop.value?.type === 'LOCKER';
  });

  const parcelShopAsConsigneeAddress = computed(() =>
    trackingDataStore.data.nearParcelShops?.find(it =>
      addressEqualsParcelShop(trackingDataStore.consigneeAddress, it)
    )
  );

  const isParcelShopDelivery = computed(
    () =>
      isRedirectedToParcelShop.value ||
      Boolean(parcelShopAsConsigneeAddress.value)
  );

  const canUseRedirectAddressCoordinates = computed(() => {
    const statusAddress = trackingDataStore.data.status?.address;
    const redirectAddress = trackingDataStore.data.redirectAddress;

    if (!statusAddress || !redirectAddress) return false;

    return (
      statusAddress &&
      !hasCoordinates(statusAddress) &&
      hasCoordinates(redirectAddress) &&
      addressEqualsParcelShop(
        statusAddress,
        statusAddressToParcelShop(redirectAddress)
      )
    );
  });

  const destinationParcelShop = computed(() => {
    if (trackingDataStore.data.status?.detail === 'PARCEL_SHOP') {
      const statusAddress = trackingDataStore.data.status.address;
      const redirectAddress = trackingDataStore.data.redirectAddress;

      if (canUseRedirectAddressCoordinates.value) {
        return getParcelShopFromStatusAddress(
          redirectAddress,
          trackingDataStore.data.nearParcelShops
        );
      }

      return getParcelShopFromStatusAddress(
        statusAddress,
        trackingDataStore.data.nearParcelShops
      );
    }
    return (
      parcelShopAsRedirectAddress.value ||
      redirectDestinationFallback.value ||
      parcelShopAsConsigneeAddress.value
    );
  });

  return {
    isRedirectedToParcelShop,
    parcelShopAsConsigneeAddress,
    parcelShopAsRedirectAddress,
    isParcelShopDelivery,
    canUseRedirectAddressCoordinates,
    destinationParcelShop,
    isRedirectedToLocker
  };
};
